import * as React from 'react'
import {
  AutocompleteItem,
  SearchForm,
  SearchInputWrapper,
  Separator,
  StyledSearchInput,
} from './styled'
import { Button } from '../Button'
import { useSearch } from '../../providers/SearchProvider'
import { algoliaClient } from '../../services/algolia'
import { createLocalStorageRecentSearchesPlugin } from '@algolia/autocomplete-plugin-recent-searches'
import { createQuerySuggestionsPlugin } from '@algolia/autocomplete-plugin-query-suggestions'
import { useRouter } from 'next/router'
import SearchClockIcon from '../../svg/SearchClock.svg'

import { createAutocomplete } from '@algolia/autocomplete-core'
import { getAlgoliaResults } from '@algolia/autocomplete-preset-algolia'
import { debouncePromise, uniqBy } from './utlis'
import { Scroller } from '../Scroller'
import { useShopData } from '../../providers/ShopDataProvider'

const { useRef, useEffect, useState, useMemo } = React

const predefinedItems = [
  { label: 'Solarium' },
  { label: 'Sonny' },
  { label: 'Diamonds' },
  { label: 'Rose Gold Chains' },
]

const debouncedSearch = debouncePromise(async (query: string) => {
  return getAlgoliaResults({
    searchClient: algoliaClient,
    queries: [
      {
        indexName: 'Storefront Search',
        query,
        params: {
          hitsPerPage: 4,
          typoTolerance: 'min',
          exactOnSingleWordQuery: 'word',
          filters: `hideFromSearch:false`,
        },
      },
    ],
  })
}, 200)

export const SearchInput = () => {
  function Autocomplete() {
    const recentSearchesPlugin = createLocalStorageRecentSearchesPlugin({
      key: 'RECENT_SEARCH',
      limit: 3,
    })

    const router = useRouter()
    const { open, loading, searchTerm, setSearchTerm } = useSearch()
    const inputRef = useRef<HTMLInputElement>(null)
    const [autocompleteState, setAutocompleteState] = useState<any>({})

    const { searchSuggestions } = useShopData().searchSettings ?? {}

    const defaultSearchSuggestions = searchSuggestions?.length
      ? searchSuggestions.map((item) => ({
          label: item,
        }))
      : predefinedItems

    const predefinedItemsPlugin = {
      getSources() {
        return [
          {
            sourceId: 'predefinedItemsPlugin',
            getItems({ query }) {
              return defaultSearchSuggestions
            },
            getItemInputValue({ item }) {
              return item.label
            },
          },
        ]
      },
    }

    const removeDuplicates = uniqBy(({ source, item }) =>
      source.sourceId === 'querySuggestionsPlugin'
        ? item.query
        : source.sourceId === 'products'
        ? item.title
        : item.label,
    )

    const sourceList = useMemo(() => {
      return (
        autocompleteState?.collections
          ?.map((collection) =>
            collection.items.map((item) => ({
              ...item,
              source: collection.source,
            })),
          )
          .flat() || []
      )
    }, [autocompleteState])

    useEffect(() => {
      if (open && inputRef.current && !loading) inputRef.current.focus()
    }, [autocompleteState, loading, open])

    const debouncedGetItems = async (
      query: string,
      setContext: (ctx: any) => void,
    ) => {
      const results = await debouncedSearch(query)
      const hits = results[0]?.hits || []

      setContext({
        results: results[0],
        hits,
      })

      return hits
    }

    const baseQuerySuggestionsPlugin = createQuerySuggestionsPlugin({
      searchClient: algoliaClient,
      indexName: 'Storefront Search Query Suggestions',
    })

    const conditionalQuerySuggestionsPlugin = {
      ...baseQuerySuggestionsPlugin,
      getSources(params) {
        if (!params.query) return []
        return baseQuerySuggestionsPlugin.getSources
          ? baseQuerySuggestionsPlugin.getSources(params)
          : []
      },
    }

    function handleSearchSubmit(query: string) {
      if (!query) return

      if (searchTerm !== query) {
        setSearchTerm(query)
      }
      router.push(
        {
          query: { ...router.query, search: query },
        },
        '',
        { shallow: true },
      )
    }

    const autocomplete = useMemo(
      () =>
        createAutocomplete({
          onStateChange({ state }) {
            setAutocompleteState(state)
          },
          plugins: [
            recentSearchesPlugin,
            conditionalQuerySuggestionsPlugin,
            predefinedItemsPlugin,
          ],
          reshape({ sourcesBySourceId }) {
            const {
              recentSearchesPlugin,
              predefinedItemsPlugin,
              querySuggestionsPlugin,
              products,
            } = sourcesBySourceId

            return removeDuplicates(
              recentSearchesPlugin,
              querySuggestionsPlugin,
              products,
              predefinedItemsPlugin,
            )
          },
          openOnFocus: true,
          initialState: {
            query: searchTerm,
          },
          onSubmit({ state }) {
            handleSearchSubmit(state.query)
          },
          getSources({ query, setContext }) {
            if (!query) {
              return [
                {
                  sourceId: 'predefinedItemsPlugin',
                  getItems() {
                    return defaultSearchSuggestions
                  },
                  getItemInputValue({ item }) {
                    return String(item.label ?? '')
                  },
                },
              ]
            }

            return [
              {
                sourceId: 'products',
                getItems() {
                  return debouncedGetItems(query, setContext)
                },
                getItemInputValue({ item }) {
                  return String(item.title ?? '')
                },
              },
            ]
          },
        }),
      [],
    )

    return (
      <SearchInputWrapper
        className="aa-Autocomplete"
        {...autocomplete.getRootProps({})}
      >
        <SearchForm
          disabled={loading}
          className="aa-Form"
          {...autocomplete.getFormProps({ inputElement: inputRef.current })}
        >
          <StyledSearchInput
            ref={inputRef}
            name="searchTerm"
            aria-label="search term"
            className="aa-Input"
            {...autocomplete.getInputProps({ inputElement: inputRef.current })}
          />

          <Scroller query={autocompleteState.query}>
            {!loading &&
              sourceList?.length > 0 &&
              sourceList.map((item: any, index: number) => {
                const source = item.source

                const itemProps = autocomplete.getItemProps({ item, source })

                return (
                  <AutocompleteItem
                    key={`${item.objectID || item.label}-${index}`}
                    className="aa-Item"
                    {...itemProps}
                    onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                      itemProps.onClick?.(event as unknown as MouseEvent)
                      const query: string =
                        source.sourceId === 'querySuggestionsPlugin'
                          ? item.query
                          : source.sourceId === 'products'
                          ? item.title
                          : item.label

                      handleSearchSubmit(query)
                    }}
                  >
                    {source.sourceId === 'recentSearchesPlugin' && (
                      <Separator>
                        <SearchClockIcon />
                      </Separator>
                    )}
                    {source.sourceId === 'querySuggestionsPlugin'
                      ? item.query
                      : source.sourceId === 'products'
                      ? item.title
                      : item.label}
                  </AutocompleteItem>
                )
              })}
          </Scroller>

          <Button level={1} type="submit">
            Search
          </Button>
        </SearchForm>
      </SearchInputWrapper>
    )
  }

  const [isClient, setIsClient] = useState(false)

  useEffect(() => {
    setIsClient(true)
  }, [])

  return isClient ? <Autocomplete /> : null
}
